import { inject, observer } from "mobx-react";
import * as React from "react";
import { HorseForSaleEndpoints } from "src/endpoints/HorseForSaleEndpoints";
import { WithdrawalOutcomeRequest } from "src/requests/HorseForSaleRequests";

import { faDollarSign, faFileInvoiceDollar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    AllPostApprovedStates,
    HorseForSaleState,
    PreDecisionActiveStates,
    PreHipNumberActiveStates,
    WithdrawalOutcome
} from "../../model/HorseForSale";
import { HorseForSaleStore } from "../../stores/HorseForSaleStore";
import EditableCard, { EditableTextareaGroup } from "../common/EditableCard";
import { FormDialog } from "../common/FormDialog";
import { SimpleConfirmation, SimpleAlert } from "../common/SimpleConfirmAlert";
import { InputGroup } from "../common/SimpleFormComponents";
import { errorMessage, successMessage } from "../common/Toast";
import { ConvertToCents } from "src/common/Methods/FileUtil";

//Props = PropsStore + PropsComponents
export interface PropsStores {
    horseForSaleStore?: HorseForSaleStore;
    horseForSaleEndpoints?: HorseForSaleEndpoints;
}
export interface PropsComponent {}
type Props = PropsStores & PropsComponent;

//State = StateComponent + State Editable Field
export interface StateComponent {
    isEditable: boolean;
    wasValidated: boolean;
    wasValidatedInvoice: boolean;
    showInvoiceAmountDialog: boolean;
}
export interface StateFields {
    invoiceAmount: string;
    withdrawalNotes: string;
}
type State = StateComponent & StateFields;

@inject("horseForSaleEndpoints")
@inject("horseForSaleStore")
@observer
export default class Withdrawal extends React.Component<Props, State> {
    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
        this.state = {
            isEditable: false,
            wasValidated: false,
            showInvoiceAmountDialog: false,
            invoiceAmount: "",
            wasValidatedInvoice: false,
            withdrawalNotes:
                this.props.horseForSaleStore!.horseForSale && this.props.horseForSaleStore!.horseForSale.outcome
                    ? (this.props.horseForSaleStore!.horseForSale && this.props.horseForSaleStore!.horseForSale.outcome.notes) || ""
                    : (this.props.horseForSaleStore!.horseForSale && this.props.horseForSaleStore!.horseForSale.earlyWithdrawalNotes) || ""
        };
    }

    areWeBeforeTheSoldState = (): boolean => {
        const { horseForSale } = this.props.horseForSaleStore!;
        return !(
            horseForSale!.state === HorseForSaleState.PROVISIONALLY_SOLD ||
            horseForSale!.state === HorseForSaleState.SOLD ||
            horseForSale!.state === HorseForSaleState.BID_IN ||
            horseForSale!.state === HorseForSaleState.WITHDRAWN_WITH_PENALTY_GENERATED ||
            horseForSale!.state === HorseForSaleState.WITHDRAWN_WITH_PENALTY_PENDING
        );
    };

    areWeInAWithdrawalDisabledState = (): boolean => {
        const { horseForSale } = this.props.horseForSaleStore!;
        return (
            horseForSale!.state === HorseForSaleState.APPLICATION ||
            horseForSale!.state === HorseForSaleState.SCHEDULED ||
            horseForSale!.state === HorseForSaleState.REJECTED ||
            horseForSale!.state === HorseForSaleState.PROVISIONALLY_SOLD ||
            horseForSale!.state === HorseForSaleState.SOLD ||
            horseForSale!.state === HorseForSaleState.BID_IN ||
            horseForSale!.hipNumber === null
        );
    };

    areWeWithdrawn = (): boolean => {
        const { horseForSale } = this.props.horseForSaleStore!;
        return (
            horseForSale!.state === HorseForSaleState.WITHDRAWN_EARLY ||
            horseForSale!.state === HorseForSaleState.WITHDRAWN_WITH_PENALTY_GENERATED ||
            horseForSale!.state === HorseForSaleState.WITHDRAWN_WITH_PENALTY_PENDING ||
            horseForSale!.state === HorseForSaleState.WITHDRAWN_WITHOUT_PENALTY
        );
    };

    generateEntryFeeInvoice = async (type: "NEW" | "UPDATE") => {
        try {
            const horseForSaleStore = this.props.horseForSaleStore!;
            const hfs = horseForSaleStore.horseForSale!;

            if (hfs.owners.length === 0) {
                SimpleAlert("Invoices cannot be generated: this Horse-for-Sale currently does not have any owners registered.");
                return;
            }

            const goAhead = await SimpleConfirmation(
                `Are you sure you'd like to ${type === "NEW" ? `generate` : `update`} the Entry Fee Invoice for ${hfs.horse.name}?
                ${type === "NEW" ? `` : ` Existing invoices will be voided.`}
            `
            );
            if (!goAhead) {
                return;
            }

            await this.props.horseForSaleStore!.generateEntryFeeInvoice(hfs.id);
            successMessage(`Invoice successfully generated.`);
        } catch (error) {
            errorMessage(error);
        }
    };

    //TODO ADD NOTE
    generateInvoiceForCommissions = async () => {
        try {
            const horseForSaleStore = this.props.horseForSaleStore!;
            const hfs = horseForSaleStore.horseForSale!;

            // TODO: We're gonig to need to take this amount and save it with the outcome first. Then we can generate the withdrawal penalty invoice.
            // const amount = Math.floor(parseFloat(this.state.invoiceAmount.replace(/,/g, "")) * 100);
            const amount = ConvertToCents(this.state.invoiceAmount);
            if (isNaN(amount)) {
                throw new Error("Invalid amount entered");
            }
            await horseForSaleStore.updateOutcome({ type: "WITHDRAWAL", vetStatement: false, fairMarketPrice: amount } as WithdrawalOutcomeRequest);

            await this.props.horseForSaleEndpoints!.generateWithdrawalPenaltyInvoice(hfs.id);
            successMessage(`Invoice successfully generated.`);
            this.closeInvoiceAmountDialog();
        } catch (error) {
            errorMessage(error);
        }
    };

    closeInvoiceAmountDialog = () => {
        this.setState({
            showInvoiceAmountDialog: false,
            invoiceAmount: ""
        });
    };

    submitInvoiceForCommissions = (e: React.FormEvent<any>) => {
        e.preventDefault();
        if (this.form && this.form.checkValidity()) {
            this.setState({ wasValidatedInvoice: false });
            this.generateInvoiceForCommissions();
        } else {
            this.setState({ wasValidatedInvoice: true });
        }
    };

    performRegularWithdrawal = async (vetStatement: string) => {
        try {
            const horseForSaleStore = this.props.horseForSaleStore!;
            const sale = horseForSaleStore.horseForSale!;

            const goAhead = await SimpleConfirmation(`Are you sure you'd like to withdraw ${sale.horse.name} from ${sale.sale.name}?`);
            if (!goAhead) {
                return;
            }

            const request: WithdrawalOutcomeRequest = {
                type: "WITHDRAWAL",
                vetStatement: vetStatement === "true",
                notes: this.state.withdrawalNotes
            };
            if (sale.outcome === null) {
                await horseForSaleStore.registerOutcome(request);
            } else {
                await horseForSaleStore.updateOutcome(request);
            }
            successMessage(`${sale.horse.name} has been withdrawn from ${sale.sale.name}.`);
            // this.setState({ isEditable: false });
        } catch (error) {
            errorMessage(error);
        }
    };

    performEarlyWithdrawal = async () => {
        try {
            const horseForSaleStore = this.props.horseForSaleStore!;
            const sale = horseForSaleStore.horseForSale!;

            const goAhead = await SimpleConfirmation(`Are you sure you'd like to withdraw ${sale.horse.name} from ${sale.sale.name}?`);
            if (!goAhead) {
                return;
            }

            await horseForSaleStore.withdrawHorseForSaleEarly(sale.id, { notes: this.state.withdrawalNotes || "" });
            successMessage(`${sale.horse.name} has been withdrawn from ${sale.sale.name} .`);
            this.setState({ isEditable: true });
        } catch (error) {
            errorMessage(error);
        }
    };

    handleChangeNotesEarlyWithdraw = async () => {
        try {
            const horseForSaleStore = this.props.horseForSaleStore!;
            const sale = horseForSaleStore.horseForSale!;

            // const goAhead = await SimpleConfirmation(
            //     `Are you sure you'd like to update withdrawal notes of ${sale.horse.name} from ${sale.sale.name}?`
            // );
            // if (!goAhead) {
            //     return;
            // }

            await horseForSaleStore.editEarlyWithdrawalNotes(sale.id, { notes: this.state.withdrawalNotes || "" });
            successMessage(`${sale.horse.name} has been withdrawn from ${sale.sale.name} .`);
            this.setState({ isEditable: false });
        } catch (error) {
            errorMessage(error);
        }
    };

    handleChangeNotesOtherWithdraw = async (vetStatement: boolean) => {
        try {
            const horseForSaleStore = this.props.horseForSaleStore!;
            const sale = horseForSaleStore.horseForSale!;

            // const goAhead = await SimpleConfirmation(
            //     `Are you sure you'd like to update withdrawal notes of ${sale.horse.name} from ${sale.sale.name}?`
            // );
            // if (!goAhead) {
            //     return;
            // }

            const request: WithdrawalOutcomeRequest = {
                type: "WITHDRAWAL",
                vetStatement: vetStatement,
                notes: this.state.withdrawalNotes
            };

            await horseForSaleStore.updateOutcome(request);
            successMessage(`${sale.horse.name} has been withdrawn from ${sale.sale.name}.`);
            this.setState({ isEditable: false });
        } catch (error) {
            errorMessage(error);
        }
    };

    editFieds = () => {
        this.setState(this.copyHorseForSaleToState({ isEditable: true }) as any);
    };

    saveEditableFields = () => {
        const horseForSaleStore = this.props.horseForSaleStore;
        if (!horseForSaleStore) {
            throw new Error(" ** No Horse For Sale Store **");
        }

        const horseForSale = horseForSaleStore.horseForSale;
        if (!horseForSale) {
            throw new Error(" ** No Horse For Sale **");
        }
        if (horseForSale.state === HorseForSaleState.WITHDRAWN_EARLY) {
            this.handleChangeNotesEarlyWithdraw();
        } else {
            this.handleChangeNotesOtherWithdraw((horseForSale.outcome as WithdrawalOutcome).withVetStatement);
        }
    };

    cancelEditableFields = () => {
        this.setState({ isEditable: false });
    };

    handleOnChange = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLSelectElement>
    ): void => {
        event.preventDefault();

        const target = event.target;
        const value = target.value;
        const name = target.name;

        const state = this.state;
        state[name] = value;
        this.setState(state);
    };

    validateForm = (): boolean => {
        return false; // this.state.newState === HorseForSaleState.APPROVED || this.state.newState === HorseForSaleState.REJECTED ? true : false;
    };

    toggleShowOnSalesReport = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        this.props.horseForSaleStore!.updateFields([
            {
                field: "showOnPublicSalesReport",
                value: !this.props.horseForSaleStore!.horseForSale!.showOnPublicSalesReport + ""
            }
        ]);
    };

    render() {
        const horseForSaleStore = this.props.horseForSaleStore;
        if (!horseForSaleStore) {
            return <div />;
        }

        const horseForSale = horseForSaleStore.horseForSale;
        if (!horseForSale) {
            return <div />;
        }

        return (
            <>
                <EditableCard
                    hideTitleInEditButton={true}
                    title="Withdrawal"
                    hideEditButton={
                        horseForSale.hipNumber === null ||
                        horseForSale!.state === HorseForSaleState.PROVISIONALLY_SOLD ||
                        horseForSale!.state === HorseForSaleState.SOLD ||
                        horseForSale!.state === HorseForSaleState.BID_IN
                    }
                    editable={this.state.isEditable}
                    onEdit={this.editFieds}
                    onCancel={this.cancelEditableFields}
                    onSave={() => this.saveEditableFields()}
                    otherHeaderButtons={
                        <>
                            {!this.areWeWithdrawn() && this.areWeBeforeTheSoldState() && horseForSale.hipNumber === null && (
                                <button
                                    className={`btn btn-info btn-sm`}
                                    type="button"
                                    onClick={
                                        PreDecisionActiveStates().some(s => s === horseForSale.state)
                                            ? _ => this.performEarlyWithdrawal()
                                            : _ => this.performRegularWithdrawal("false")
                                    }
                                >
                                    Withdraw
                                </button>
                            )}
                        </>
                    }
                >
                    {/* If hip numbers haven't been generated yet */}
                    {horseForSale.hipNumber === null && (
                        <>
                            <div className="row mb-3">
                                <div className="form-group col-sm-12 col-md-12">
                                    {PreHipNumberActiveStates().some(s => s === horseForSale.state) && (
                                        <label>
                                            {PreDecisionActiveStates().some(s => s === horseForSale.state) && (
                                                <>
                                                    Click <em>Withdraw</em> above to early withdraw the Horse-for-Sale. No penalties apply since it
                                                    has not yet been inspected or approved.
                                                </>
                                            )}
                                            {!PreDecisionActiveStates().some(s => s === horseForSale.state) && (
                                                <>
                                                    Click <em>Withdraw</em> above to withdraw the Horse-for-Sale. Since hip numbers have not yet been
                                                    generated, commission penalties will not be applicable but the entry fee may still apply.
                                                </>
                                            )}
                                        </label>
                                    )}
                                </div>
                            </div>
                            <div className="form-row">
                                {/* Early Withdrawn*/}
                                <EditableTextareaGroup
                                    label="Notes"
                                    rows={5}
                                    name="withdrawalNotes"
                                    className="col-md-12"
                                    inputClassName="not-required"
                                    editable={this.state.isEditable && this.areWeBeforeTheSoldState()}
                                    editableValue={this.state.withdrawalNotes || ""}
                                    nonEditableValue={
                                        horseForSale.state === HorseForSaleState.WITHDRAWN_EARLY
                                            ? horseForSale.earlyWithdrawalNotes || ""
                                            : (horseForSale.outcome && horseForSale.outcome.notes) || ""
                                    }
                                    onChange={this.handleOnChange}
                                />
                            </div>
                        </>
                    )}
                    {horseForSale.hipNumber !== null && (
                        <>
                            <div className="row mr-0 pr-0">
                                <div className="form-group col-12">
                                    <label htmlFor="showOnPublicSalesReport">
                                        <input
                                            disabled={!this.areWeWithdrawn()}
                                            name="showOnPublicSalesReport"
                                            id="showOnPublicSalesReport"
                                            checked={horseForSale.showOnPublicSalesReport}
                                            onChange={this.toggleShowOnSalesReport}
                                            type="checkbox"
                                            className="mr-3"
                                        />
                                        Show on roster
                                    </label>
                                </div>
                                <div className="form-group col-sm-12 col-md-8">
                                    <div className="btn-group btn-group-toggle" data-toggle="buttons">
                                        <label
                                            className={`btn ${
                                                horseForSale.state === HorseForSaleState.WITHDRAWN_WITHOUT_PENALTY &&
                                                (horseForSale.outcome as WithdrawalOutcome).withVetStatement === true
                                                    ? `btn-info`
                                                    : `btn-outline-info`
                                            }
                                                ${
                                                    (!this.state.isEditable && !this.areWeInAWithdrawalDisabledState()) ||
                                                    this.areWeInAWithdrawalDisabledState()
                                                        ? `disabled`
                                                        : ``
                                                }`}
                                        >
                                            <input
                                                type="radio"
                                                name="withdrawWithOrwithoutVet"
                                                value="true"
                                                checked={
                                                    horseForSale.state === HorseForSaleState.WITHDRAWN_WITHOUT_PENALTY &&
                                                    (horseForSale.outcome as WithdrawalOutcome).withVetStatement === true
                                                }
                                                onChange={e => this.performRegularWithdrawal(e.currentTarget.value)}
                                                disabled={
                                                    (!this.state.isEditable && !this.areWeInAWithdrawalDisabledState()) ||
                                                    this.areWeInAWithdrawalDisabledState()
                                                }
                                            />{" "}
                                            Withdraw with Vet Statement
                                        </label>
                                        <label
                                            className={`btn
                                                ${
                                                    // TODO: I think this should include PENTALTY_GENERATED too
                                                    horseForSale.state === HorseForSaleState.WITHDRAWN_WITH_PENALTY_PENDING &&
                                                    (horseForSale.outcome as WithdrawalOutcome).withVetStatement === false
                                                        ? `btn-info`
                                                        : `btn-outline-info`
                                                } 
                                                ${
                                                    (!this.state.isEditable && !this.areWeInAWithdrawalDisabledState()) ||
                                                    this.areWeInAWithdrawalDisabledState()
                                                        ? `disabled`
                                                        : ``
                                                }`}
                                        >
                                            <input
                                                type="radio"
                                                name="withdrawWithOrwithoutVet"
                                                value="false"
                                                checked={
                                                    // TODO: I think this should include PENTALTY_GENERATED too
                                                    horseForSale.state === HorseForSaleState.WITHDRAWN_WITH_PENALTY_PENDING &&
                                                    (horseForSale.outcome as WithdrawalOutcome).withVetStatement === false
                                                }
                                                onChange={e => this.performRegularWithdrawal(e.currentTarget.value)}
                                                disabled={
                                                    (!this.state.isEditable && !this.areWeInAWithdrawalDisabledState()) ||
                                                    this.areWeInAWithdrawalDisabledState()
                                                }
                                            />{" "}
                                            Withdraw without Vet Statement
                                        </label>
                                    </div>
                                </div>
                                {horseForSale.state === HorseForSaleState.WITHDRAWN_WITH_PENALTY_PENDING && (
                                    <div className="form-group col-sm-12 col-md-4 pr-0 pt-2 text-right">
                                        <button className="btn btn-info mt-4" onClick={() => this.setState({ showInvoiceAmountDialog: true })}>
                                            <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2" /> Generate Invoice for Commission
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="form-row">
                                {/* Other Withdrawn*/}
                                <EditableTextareaGroup
                                    label="Notes"
                                    rows={5}
                                    name="withdrawalNotes"
                                    className="col-md-12"
                                    inputClassName="not-required"
                                    editable={this.state.isEditable && !this.areWeInAWithdrawalDisabledState()}
                                    editableValue={this.state.withdrawalNotes || ""}
                                    nonEditableValue={(horseForSale.outcome && horseForSale.outcome.notes) || ""}
                                    onChange={this.handleOnChange}
                                />
                            </div>
                        </>
                    )}
                </EditableCard>
                <FormDialog
                    title="Invoice for Commission"
                    modalSize="modal-md"
                    show={this.state.showInvoiceAmountDialog}
                    onClose={() => this.closeInvoiceAmountDialog()}
                >
                    <form
                        className={"px-2 needs-validation " + (this.state.wasValidatedInvoice === true ? "was-validated" : "")}
                        noValidate={true}
                        onSubmit={this.submitInvoiceForCommissions}
                        ref={ref => (this.form = ref)}
                        id="invoiceForCommissions"
                    >
                        <div className="form-row">
                            <InputGroup
                                label="Enter Assessed Fair Market Value"
                                type="number"
                                disabled={false}
                                isRequired={true}
                                name="invoiceAmount"
                                className="form-group col-md-12"
                                value={this.state.invoiceAmount || ""}
                                onChange={e => this.setState({ invoiceAmount: e.target.value })}
                                icon={<FontAwesomeIcon icon={faDollarSign} />}
                            />
                        </div>
                        <div className="form-row text-right">
                            <button type="button" className="btn btn-secondary mr-auto" onClick={() => this.closeInvoiceAmountDialog()}>
                                Cancel
                            </button>
                            <button type="submit" form="invoiceForCommissions" className="btn btn-primary">
                                Generate
                            </button>
                        </div>
                    </form>
                </FormDialog>
            </>
        );
    }

    private copyHorseForSaleToState(otherFields: Partial<State> = {}): Partial<State> {
        const horseForSale = this.props.horseForSaleStore!.horseForSale;
        if (!horseForSale) {
            return {};
        }

        const updatedState: Partial<State> = Object.assign(
            {
                withdrawalNotes:
                    horseForSale.state === HorseForSaleState.WITHDRAWN_EARLY
                        ? horseForSale.earlyWithdrawalNotes || ""
                        : (horseForSale.outcome && horseForSale.outcome.notes) || ""
            },
            otherFields
        );

        return updatedState;
    }
}



// WEBPACK FOOTER //
// ./src/components/horses-for-sale/Withdrawal.tsx